import { useEffect, useState } from "react";
import { History, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ProfileAudit — read-only timeline of who did what on a single profile.
 *
 * Surfaces helper invites/accepts/revokes, permission changes, and helper
 * history reads. Parents/guardians can use this to verify therapist or
 * helper activity matches what they expect.
 *
 * Uses /api/profiles/{id}/audit (Block 6 endpoint, owner-only).
 */

const ACTION_LABELS = {
  helper_invited:              "Invited",
  helper_accepted:             "Accepted",
  helper_revoked:              "Revoked",
  helper_permissions_changed:  "Permissions changed",
  helper_viewed_history:       "Viewed history",
};

function fmtTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ProfileAudit({ profile }) {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(true);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/profiles/${profile.id}/audit?limit=50`);
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (err) {
      console.warn("ProfileAudit: list failed", err?.response?.status || err?.message);
    }
    setBusy(false);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [profile.id]);

  return (
    <div className="brutal-card bg-white p-4 mb-2" data-testid={`audit-panel-${profile.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-9 h-9 rounded-xl border-[2.5px] border-[color:var(--ink)] flex items-center justify-center text-xl"
          style={{ background: profile.color }}
          aria-hidden
        >
          {profile.avatar}
        </span>
        <div className="flex-1 min-w-0">
          <div className="display font-extrabold truncate">{profile.name}</div>
          <div className="text-xs text-[color:var(--ink-soft)]">
            {count} action{count === 1 ? "" : "s"} on file
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="pill-btn"
          disabled={busy}
          data-testid={`audit-refresh-${profile.id}`}
          aria-label="Refresh audit log"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {busy ? (
        <div className="text-xs text-[color:var(--ink-soft)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-[color:var(--ink-soft)] italic">
          No activity yet. Helper invites, accepts, history reads, and
          permission changes will show up here.
        </div>
      ) : (
        <ul className="space-y-1 text-sm" data-testid={`audit-list-${profile.id}`}>
          {items.map((e, i) => (
            <li
              key={`${e.ts}-${i}`}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg border-[2px] border-[color:var(--ink)] bg-[color:var(--paper-muted)]"
              data-testid={`audit-event-${i}`}
            >
              <History size={12} className="shrink-0" />
              <span className="font-bold display text-[13px]">{ACTION_LABELS[e.action] || e.action}</span>
              <span className="flex-1 truncate text-[color:var(--ink-soft)]">
                {e.actor_email || "—"}
                {e.target ? ` → ${typeof e.target === 'string' ? e.target : ''}` : ""}
              </span>
              <span className="text-[10px] text-[color:var(--ink-soft)] whitespace-nowrap">
                {fmtTs(e.ts)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
